import React from 'react';
import { X, Eye, Calendar, Mail, Phone, Shield, Clock } from 'lucide-react';

const formatPermissionName = (permission) => {
  const permissionLabels = {
    'access_dashboard': 'Dashboard',
    'access_create_bond': 'Create Bond',
    'access_bond_management': 'Bond Management',
    'access_documents': 'Documents',
    'access_announcements': 'Announcements',
    'access_sub_accounts': 'Sub Accounts',
    'access_role_management': 'Role Management',
    'access_audit_logs': 'Audit Logs',
    'access_subscription_management': 'Subscription Management',
    'access_create_sub_agreement': 'Create Sub Agreement',
    'access_sub_agreement_management': 'Sub Agreement Management',
    'access_bond_applications': 'Bond Applications',
    'access_bond_holdings': 'Bond Holdings',
    'access_issuance_payment_management': 'Issuance Payment Management',
    'access_coupon_management': 'Coupon Management',
    'access_maturity_management': 'Maturity Management',
    'access_token_holder': 'Bond Holder',
    'access_create_loc': 'Create LOC',
    'access_loc_management': 'LOC Management',
    'access_submit_sub_agreement': 'Submit Sub Agreement',
    'access_receive_bond_token': 'Receive Bond Token',
    'access_bond_token_allocation': 'Bond Token Allocation',
    'access_sub_investors': 'Sub Investors',
    'access_sub_investor_loc': 'Sub Investor LOC',
    'access_wallet_management': 'Wallet Management',
    'access_reports': 'Reports',
    'access_user_management': 'User Management',
    'access_submit_loc': 'Submit LOC',
    'access_assigned_bonds': 'Assigned Bonds',
    'access_bond_holders': 'Bond Holders'
  };

  return permissionLabels[permission] || permission
    .replace(/^access_/, '')
    .split('_')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

const SubAccountDetailsModal = ({ 
  isOpen, 
  onClose, 
  selectedSubAccount, 
  getSubAccountDisplayName,
  getStatusBadge
}) => {
  if (!isOpen || !selectedSubAccount) return null;

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });
  };

  const getAssignedRolePermissions = () => {
    if (selectedSubAccount.assignedRoleId?.permissions) {
      return selectedSubAccount.assignedRoleId.permissions;
    }
    return [];
  };

  const getAssignedRoleName = () => {
    if (selectedSubAccount.assignedRoleId?.name) {
      return selectedSubAccount.assignedRoleId.name;
    }
    return null;
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-light-gray">
          <div className="flex items-center">
            <Eye className="w-6 h-6 mr-3 text-primary-600" />
            <h3 className="text-lg font-medium text-gray-900">
              Sub Account Details
            </h3>
          </div>
          <button
            onClick={onClose}
            className="transition-colors"
            style={{ color: '#9ca3af' }}
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6">
          {/* Sub Account Header */}
          <div className="p-6 mb-6 rounded-lg bg-secondary-50">
            <div className="flex items-center">
              <div className="flex-shrink-0 w-16 h-16">
                <div className="flex items-center justify-center w-16 h-16 rounded-full bg-primary-100">
                  <span className="text-xl font-medium text-primary-600">
                    {getSubAccountDisplayName(selectedSubAccount).charAt(0).toUpperCase()}
                  </span>
                </div>
              </div>
              <div className="ml-6">
                <h2 className="text-xl font-semibold text-gray-900">
                  {getSubAccountDisplayName(selectedSubAccount)}
                </h2>
                <div className="flex items-center mt-1">                  
                  {getStatusBadge(selectedSubAccount)}
                </div>
                <p className="mt-1 text-sm" style={{ color: '#6b7280' }}>
                  Sub Account ID: {selectedSubAccount._id}
                </p>
              </div>
            </div>
          </div>

          {/* Personal Information */}
          <div className="mb-6">
            <h3 className="flex items-center mb-4 text-lg font-medium text-gray-900">
              <Shield className="w-5 h-5 mr-2 text-primary-600" />
              Personal Information
            </h3>
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <div className="p-4 bg-white border rounded-lg border-light-gray">
                <div className="flex items-center mb-2">
                  <Mail className="w-4 h-4 mr-2 text-dark-gray" />
                  <span className="text-sm font-medium text-dark-gray">Email Address</span>
                </div>
                <p className="text-sm text-secondary-900">{selectedSubAccount.email}</p>
              </div>

              <div className="p-4 bg-white border rounded-lg border-light-gray">
                <div className="flex items-center mb-2">
                  <Phone className="w-4 h-4 mr-2 text-dark-gray" />
                  <span className="text-sm font-medium text-dark-gray">Phone Number</span>
                </div>
                <p className="text-sm text-secondary-900">
                  {selectedSubAccount.personalInfo?.phoneNumber || 'N/A'}
                </p>
              </div>

              <div className="p-4 bg-white border rounded-lg border-light-gray">
                <div className="flex items-center mb-2">
                  <Shield className="w-4 h-4 mr-2 text-dark-gray" />
                  <span className="text-sm font-medium text-dark-gray">First Name</span>
                </div>
                <p className="text-sm text-secondary-900">
                  {selectedSubAccount.personalInfo?.firstName || 'N/A'}
                </p>
              </div>

              <div className="p-4 bg-white border rounded-lg border-light-gray">
                <div className="flex items-center mb-2">
                  <Shield className="w-4 h-4 mr-2 text-dark-gray" />
                  <span className="text-sm font-medium text-dark-gray">Last Name</span>
                </div>
                <p className="text-sm text-secondary-900">
                  {selectedSubAccount.personalInfo?.lastName || 'N/A'}
                </p>
              </div>
            </div>
          </div>

          {/* Account Information */}
          <div className="mb-6">
            <h3 className="flex items-center mb-4 text-lg font-medium text-gray-900">
              <Clock className="w-5 h-5 mr-2 text-primary-600" />
              Account Information
            </h3>
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <div className="p-4 bg-white border rounded-lg border-light-gray">
                <div className="flex items-center mb-2">
                  <Calendar className="w-4 h-4 mr-2 text-dark-gray" />
                  <span className="text-sm font-medium text-dark-gray">Created Date</span>
                </div>
                <p className="text-sm text-secondary-900">
                  {formatDate(selectedSubAccount.createdAt)}
                </p>
              </div>

              <div className="p-4 bg-white border rounded-lg border-light-gray">
                <div className="flex items-center mb-2">
                  <Clock className="w-4 h-4 mr-2 text-dark-gray" />
                  <span className="text-sm font-medium text-dark-gray">Last Login</span>
                </div>
                <p className="text-sm text-secondary-900">
                  {formatDate(selectedSubAccount.lastLoginAt)}
                </p>
              </div>

              <div className="p-4 bg-white border rounded-lg border-light-gray">
                <div className="flex items-center mb-2">
                  <Shield className="w-4 h-4 mr-2 text-dark-gray" />
                  <span className="text-sm font-medium text-dark-gray">Account Status</span>
                </div>
                <div className="flex items-center">
                  {getStatusBadge(selectedSubAccount)}
                </div>
              </div>

              <div className="p-4 bg-white border rounded-lg border-light-gray">
                <div className="flex items-center mb-2">
                  <Shield className="w-4 h-4 mr-2 text-dark-gray" />
                  <span className="text-sm font-medium text-dark-gray">Account Type</span>
                </div>
                <p className="text-sm text-secondary-900">Sub Account</p>
              </div>
            </div>
          </div>

          {/* Permissions Information */}
          <div className="mb-6">
            <h3 className="flex items-center mb-4 text-lg font-medium text-gray-900">
              <Shield className="w-5 h-5 mr-2 text-primary-600" />
              Assigned Role & Permissions
            </h3>
            {getAssignedRoleName() ? (
              <div className="p-4 border rounded-lg bg-info-50 border-info-200">
                <div className="flex items-center mb-3">
                  <Shield className="w-4 h-4 mr-2 text-info-600" />
                  <span className="text-sm font-medium text-info-800">
                    Role: {getAssignedRoleName()}
                  </span>
                </div>
                <div>
                  <p className="mb-2 text-xs font-medium text-info-700">Granted Permissions:</p>
                  <div className="flex flex-wrap gap-2">
                    {getAssignedRolePermissions().length > 0 ? (
                      getAssignedRolePermissions().map((permission, index) => (
                        <span
                          key={index}
                          className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-info-100 text-info-800"
                        >
                          {formatPermissionName(permission)}
                        </span>
                      ))
                    ) : (
                      <span className="text-sm text-info-600">No permissions assigned</span>
                    )}
                  </div>
                </div>
              </div>
            ) : (
              <div className="p-4 border rounded-lg bg-warning-50 border-warning-200">
                <div className="flex items-center mb-2">
                  <Shield className="w-4 h-4 mr-2 text-warning-400" />
                  <span className="text-sm font-medium text-warning-800">No Role Assigned</span>
                </div>
                <p className="text-sm text-warning-700">
                  This sub-account does not have a custom role assigned. 
                  Assign a role through the Edit Sub Account or Role Management page.
                </p>
              </div>
            )}
          </div>

          {/* Additional Information */}
          {selectedSubAccount.registrationStatus && (
            <div className="mb-6">
              <h3 className="flex items-center mb-4 text-lg font-medium text-gray-900">
                <Shield className="w-5 h-5 mr-2 text-primary-600" />
                Registration Status
              </h3>
              <div className="p-4 bg-white border rounded-lg border-light-gray">
                <div className="flex items-center mb-2">
                  <Shield className="w-4 h-4 mr-2 text-dark-gray" />
                  <span className="text-sm font-medium text-dark-gray">Status</span>
                </div>
                <p className="text-sm capitalize text-secondary-900">
                  {selectedSubAccount.registrationStatus.replace('_', ' ')}
                </p>
              </div>
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex justify-end pt-4 space-x-3 border-t border-light-gray">
            <button
              onClick={onClose}
              className="btn-primary"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SubAccountDetailsModal; 